import { quat } from "gl-matrix";
import { useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { CameraType, selectCamera } from "features/render";
import { isTopDownRotation } from "features/render/utils";

import { selectAlignmentView } from "../selectors";
import { alignmentActions } from "../slice";
import { AlignmentView } from "../types";

/**
 * Sync alignment view with camera if somebody switches camera type from ortho shortcut (or some other way)
 */
export function useSyncAlignmentView() {
    const camera = useAppSelector(selectCamera);
    const alignmentView = useAppSelector(selectAlignmentView);
    const alignmentViewRef = useRef(alignmentView);
    const dispatch = useAppDispatch();
    alignmentViewRef.current = alignmentView;

    const [rotation, setRotation] = useState(camera.type === CameraType.Pinhole ? undefined : camera.goTo?.rotation);
    useEffect(() => {
        if (camera.type === CameraType.Pinhole) {
            // We only care about rotation when we're in ortho mode
            setRotation(undefined);
            return;
        }

        setRotation((prevRotation) => {
            const newRotation = camera.goTo?.rotation;
            // Avoid changing rotation if the numbers are the same
            if (prevRotation && newRotation && quat.exactEquals(prevRotation, newRotation)) {
                return prevRotation;
            }
            return newRotation;
        });
    }, [camera]);

    useEffect(() => {
        if (camera.type === CameraType.Orthographic && rotation) {
            const isTopDown = isTopDownRotation(rotation);
            if (alignmentViewRef.current !== AlignmentView.OrthoTopDown && isTopDown) {
                dispatch(alignmentActions.setAlignmentView(AlignmentView.OrthoTopDown));
            } else if (alignmentViewRef.current !== AlignmentView.OrthoCrossSection && !isTopDown) {
                dispatch(alignmentActions.setAlignmentView(AlignmentView.OrthoCrossSection));
            }
        } else if (camera.type === CameraType.Pinhole && alignmentViewRef.current !== AlignmentView.Pinhole) {
            dispatch(alignmentActions.setAlignmentView(AlignmentView.Pinhole));
        }
    }, [dispatch, camera.type, rotation]);
}
